import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { sets } from '../utils/api';
import SetGallery from '../components/SetGallery';
import SharedLayout from '../components/SharedLayout';

const SharedSetView = () => {
  const { shareId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = searchParams.get('view') || 'grid';
  const [setData, setSetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const defaultSortPreferences = {
    grid: { field: 'purchase_date', direction: 'desc' },
    carousel: { field: 'purchase_date', direction: 'desc' },
    timeline: { field: 'purchase_date', direction: 'desc' }
  };
  const [sortPreferences, setSortPreferences] = useState(defaultSortPreferences);

  // Initial fetch and sort preferences update
  useEffect(() => {
    const fetchSetData = async () => {
      try {
        const response = await sets.getSharedSet(shareId);
        if (!response) {
          throw new Error('Set not found');
        }
        
        console.log('Shared set response:', response); // Debug log
        
        // Pass through all fields from the API response
        setSetData({
          ...response,
          coverImagePath: response.cover_image_path,
          sharedBy: response.owner?.company_name 
            ? `${response.sharedby} (${response.owner.company_name})`
            : response.sharedby
        });
        setSortPreferences(response.sort_preferences || defaultSortPreferences);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSetData();
  }, [shareId]);

  const handleViewModeChange = (mode) => {
    setSearchParams({ view: mode });
  };

  if (loading) {
    return (
      <SharedLayout>
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </SharedLayout>
    );
  }

  if (error) {
    return (
      <SharedLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 text-red-600 p-4 rounded-md">
            {error === 'Not found' ? 'This set is no longer available.' : `Error: ${error}`}
          </div>
        </div>
      </SharedLayout>
    );
  }

  // Construct the full URL for sharing
  const fullUrl = `${window.location.origin}/shared/${shareId}`;
  
  // Get the cover image URL
  const coverImageUrl = `${window.location.origin}${setData?.coverImagePath}`;

  return (
    <SharedLayout>
      <Helmet>
        <title>{setData?.name || 'Coin Set'} - NumisNavigator</title>
        <meta name="description" content={`View this curated collection of coins: ${setData?.name || 'Coin Set'}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={fullUrl} />
        <meta property="og:title" content={`${setData?.name || 'Coin Set'} - NumisNavigator`} />
        <meta property="og:description" content={`View this curated collection of ${setData?.totalCoins || ''} coins: ${setData?.name || 'Coin Set'}`} />
        <meta property="og:image" content={coverImageUrl} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={fullUrl} />
        <meta name="twitter:title" content={`${setData?.name || 'Coin Set'} - NumisNavigator`} />
        <meta name="twitter:description" content={`View this curated collection of ${setData?.totalCoins || ''} coins: ${setData?.name || 'Coin Set'}`} />
        <meta name="twitter:image" content={coverImageUrl} />
      </Helmet>
      <SetGallery 
        setId={shareId} 
        isShared={true} 
        initialViewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        sortPreferences={sortPreferences}
        refreshTrigger={sortPreferences} // Trigger refresh when sort preferences change
        setInfo={setData}
        initialCoins={setData?.coins}
        totalCoins={setData?.totalCoins}
      />
    </SharedLayout>
  );
};

export default SharedSetView;
